import { migrateLines, newId, withQuantity } from "@/utils/estimate";
import { MONTHS_PER_YEAR } from "@/utils/pricing";

const persistServices = (state) =>
  localStorage.setItem("services", JSON.stringify(state.addedServices));

const persistGroups = (state) =>
  localStorage.setItem("groups", JSON.stringify(state.groups));

export const mutations = {
  ADD_SERVICE(state, service) {
    const quantity = Number(service.quantity) > 0 ? service.quantity : 1;
    const unitMonthlyCost =
      service.unitMonthlyCost === undefined
        ? (Number(service.monthlyCost) || 0) / quantity
        : service.unitMonthlyCost;

    state.addedServices.push({
      ...service,
      id: service.id || newId(),
      quantity,
      unitMonthlyCost,
      monthlyCost: unitMonthlyCost * quantity,
      totalCost: unitMonthlyCost * quantity * MONTHS_PER_YEAR,
    });
    persistServices(state);
  },

  UPDATE_SERVICE(state, service) {
    const index = state.addedServices.findIndex((s) => s.id === service.id);
    if (index === -1) return;
    state.addedServices[index] = { ...state.addedServices[index], ...service };
    persistServices(state);
  },

  SET_SERVICE_QUANTITY(state, { id, quantity }) {
    const index = state.addedServices.findIndex((s) => s.id === id);
    if (index === -1) return;
    state.addedServices[index] = withQuantity(
      state.addedServices[index],
      quantity
    );
    persistServices(state);
  },

  DUPLICATE_SERVICE(state, id) {
    const source = state.addedServices.find((s) => s.id === id);
    if (!source) return;
    const index = state.addedServices.indexOf(source);
    state.addedServices.splice(index + 1, 0, {
      ...JSON.parse(JSON.stringify(source)),
      id: newId(),
    });
    persistServices(state);
  },

  REMOVE_SERVICE(state, service) {
    const id = typeof service === "string" ? service : service?.id;
    if (!id) return;

    state.addedServices = state.addedServices.filter((s) => s.id !== id);
    persistServices(state);

    state.groups.forEach((group) => {
      group.services = (group.services || []).filter((s) => s.id !== id);
    });
    persistGroups(state);
  },

  CLEAR_SERVICES(state) {
    state.addedServices = [];
    persistServices(state);
    state.groups.forEach((group) => {
      group.services = [];
    });
    persistGroups(state);
  },

  GROUPS(state, newGroup) {
    state.groups.push(newGroup);
    persistGroups(state);
  },

  ADD_SERVICE_TO_GROUP(state, { group, service }) {
    const index = state.groups.findIndex((g) => g.name === group.name);
    if (index === -1) return;

    const services = state.groups[index].services || [];
    if (!services.some((s) => s.id === service.id)) {
      services.push({ id: service.id, name: service.name });
    }
    state.groups[index].services = services;
    persistGroups(state);
  },

  SET_GROUPS(state, groups) {
    // Groups used to embed full copies of each line; keep only the reference
    // so a group always reflects the current price of its services.
    state.groups = (Array.isArray(groups) ? groups : []).map((group) => ({
      ...group,
      services: (group.services || [])
        .filter((service) => service && service.id)
        .map((service) => ({ id: service.id, name: service.name })),
    }));
  },

  SET_SERVICES(state, services) {
    state.addedServices = migrateLines(services);
  },

  DELETE_GROUP(state, group) {
    state.groups = state.groups.filter((g) => g.name !== group.name);
    persistGroups(state);
  },

  UPDATE_GROUP(state, group) {
    const index = state.groups.findIndex((g) => g.name === group.name);
    if (index === -1) return;

    // Callers hand back groups read from `groupsWithTotals`, which carries
    // resolved lines and a derived total; store only the persistent fields.
    const { totalMonthlyCost, ...rest } = group;
    void totalMonthlyCost;

    state.groups[index] = {
      ...rest,
      services: (group.services || []).map((service) => ({
        id: service.id,
        name: service.name,
      })),
    };
    persistGroups(state);
  },

  updateExchangeRates(state, exchangeRates) {
    state.exchangeRates = exchangeRates;
  },

  updateCurrentCurrency(state, newCurrency) {
    state.currentCurrency = newCurrency;
  },
};
